import { createContext, useCallback, useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { ApiResult } from 'src/api/types';
import DataGrid from './components/DataGrid';
import DateRangePicker from './components/DateRangePicker';
import ExportButton from './components/ExportButton';
import useResetFlagCleanup from './hooks/useResetFlagCleanup';
import useUpdateTranslatePrefix from './hooks/useUpdateTranslatePrefix';
import { ContextTypes } from './types/Context';
import { ProviderPropsTypes } from './types/ProviderPropsTypes';
import SearchAndResetBox from './components/SearchAndResetBox';

//Create Context
const PageConfigProviderContext = createContext<ContextTypes<any, any> | undefined>(undefined);

//useContext
const usePageConfigProviderContext = <FilterFieldsType, GetListRes>() => {
  const context = useContext<ContextTypes<FilterFieldsType, GetListRes> | undefined>(
    PageConfigProviderContext
  );
  if (!context) {
    throw new Error(
      'PageConfigProvider components must be rendered within PageConfigProviderContext'
    );
  }
  return context;
};

//Provider
const PageConfigProvider = <FilterFieldsType, GetListRes>(
  props: ProviderPropsTypes<FilterFieldsType>
) => {
  const {
    children,

    //translate
    translatePrefix,

    //filter
    initFields,

    //params
    getListParams,

    //table query
    enabledQuery,
    getTableDataApiFunc,

    //common use case - click search button before init fetch handle
    shouldClickBeforeInitFetch,
  } = props;

  //update translatePrefix
  useUpdateTranslatePrefix(translatePrefix);

  //page, filter, params, collapsiable filter control
  const [page, setPage] = useState(0);
  const [fields, setFields] = useState<FilterFieldsType>(initFields);
  const [listParams, setListParams] = useState(getListParams(fields));

  //common use case - click search button before init fetch handle
  const [isSearchButtonClicked, setIsSearchButtonClicked] = useState(false);
  //reset flag - used in DateRangePicker
  const [isResetClicked, setIsResetClicked] = useState(false);

  //Page Change
  const onPageChange = (page: number) => {
    setPage(page);
    setListParams({ ...listParams, page });
  };

  //search button control
  const onFilterSearch = () => {
    if (shouldClickBeforeInitFetch && !isSearchButtonClicked) {
      setIsSearchButtonClicked(true);
    }
    const filterFieldsData = getListParams(fields);
    setPage(0);
    setListParams({
      ...filterFieldsData,
      page: 0,
      currentTime: Date.now(),
    });
  };

  const onFilterReset = useCallback(() => {
    setIsResetClicked(true);
    setFields(initFields);
  }, [initFields]);

  //isResetClicked is used in DateRangePicker
  useResetFlagCleanup(isResetClicked, setIsResetClicked);

  //params
  const { currentTime, ...cleanParams } = listParams;

  const getEnabledQuery = () => {
    if (enabledQuery !== undefined) {
      return enabledQuery;
    }
    if (shouldClickBeforeInitFetch) {
      return isSearchButtonClicked;
    }
    return undefined;
  };

  //table query
  const { data, isLoading } = useQuery<ApiResult<GetListRes>>({
    queryKey: [`${translatePrefix}Table`, listParams],
    queryFn: () => getTableDataApiFunc(cleanParams),
    enabled: getEnabledQuery(),
  });

  const list = data ?? { rows: [], count: 0 };

  return (
    <PageConfigProviderContext.Provider
      value={{
        //translate
        translatePrefix,

        //page
        page,
        onPageChange,

        //filter
        fields,
        setFields,

        //search button control
        onFilterSearch,
        onFilterReset,
        isResetClicked,

        //params
        listParams,
        getListParams,
        setListParams,

        //table api
        isLoading,
        list,

        //common use case - click search button before init fetch handle
        shouldClickBeforeInitFetch,
        isSearchButtonClicked,
      }}
    >
      {children}
    </PageConfigProviderContext.Provider>
  );
};

PageConfigProvider.ExportButton = ExportButton;
PageConfigProvider.DateRangePicker = DateRangePicker;
PageConfigProvider.DataGrid = DataGrid;
PageConfigProvider.SearchAndResetBox = SearchAndResetBox;

export default PageConfigProvider;
export { usePageConfigProviderContext };
